import React, { useEffect, useMemo, useState } from "react";
import { UserPlus } from "lucide-react";
import BaseSelect from "../../Base/BaseSelect";
import { Service } from "../../../models/ServiceModel";
import AccessibleDatePicker from "./AccessibleDatePicker";
import AccessibleTimePicker from "./AccessibleTimePicker";
import {
  getMinServiceDateYmd,
  isServiceDateTimeInPast,
} from "../../../utils/serviceScheduleValidation";
import { isAdicionalEnabledForCompany } from "../../../config/adicionalCompany";
import useAdicionalCatalogService, {
  AdicionalEvents,
  AreaItem,
  ElAbraGroupItem,
  ManagementItem,
  SuperintendencyItem,
} from "../../../services/useAdicionalCatalogService";
import type { ServiceResponse } from "../../../hooks/useFetch";
import showToast from "../../../utils/toast";
import ElAbraGroupModal from "../Adicional/ElAbraGroupModal";
import AdicionalCatalogModal, {
  AdicionalModalKind,
} from "../Adicional/AdicionalCatalogModal";
import "../styles/ServiceFormStep1.css";

const compareEs = (a: string, b: string) =>
  a.localeCompare(b, "es", { sensitivity: "base" });

type ElAbraCatalogKind = Extract<
  AdicionalModalKind,
  "management" | "superintendency" | "area"
>;

type Props = {
  value: Service;
  onChange: (field: keyof Service, value: any) => void;
  onPatch?: (patch: Partial<Service>) => void;
  companies: { value: string; label: string }[];
};

const ServiceFormStep1: React.FC<Props> = ({ value, onChange, onPatch, companies }) => {
  const {
    listElAbraGroups,
    listManagements,
    listSuperintendencies,
    listAreas,
    storeManagement,
    storeSuperintendency,
    storeArea,
    storeElAbraGroup,
    fetching,
  } = useAdicionalCatalogService();
  const [elAbraGroups, setElAbraGroups] = useState<ElAbraGroupItem[]>([]);
  const [managements, setManagements] = useState<ManagementItem[]>([]);
  const [superintendencies, setSuperintendencies] = useState<SuperintendencyItem[]>([]);
  const [areas, setAreas] = useState<AreaItem[]>([]);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [catalogModalKind, setCatalogModalKind] = useState<ElAbraCatalogKind | null>(null);
  const [selectedCatalogItem, setSelectedCatalogItem] = useState<{
    kind: ElAbraCatalogKind;
    id: number;
  } | null>(null);

  const minDateYmd = getMinServiceDateYmd();
  const isPastDate = Boolean(value.service_date) && String(value.service_date) < minDateYmd;
  const dateTimeInPast = isServiceDateTimeInPast(
    value.service_date,
    value.service_time
  );

  const companyName =
    companies.find((c) => String(c.value) === String(value.company))?.label ?? null;
  const showElAbraFields = isAdicionalEnabledForCompany(companyName);

  const reloadElAbraGroups = (selectId?: number) => {
    listElAbraGroups(1, {
      onSuccess: (resp: ServiceResponse) => {
        const data = resp?.data ?? {};
        const list = Array.isArray(data.el_abra_groups)
          ? data.el_abra_groups
          : Array.isArray(data)
            ? data
            : [];
        setElAbraGroups(list);

        if (selectId !== undefined) {
          const group = list.find((g: ElAbraGroupItem) => g.id === selectId);
          if (group) {
            onPatch?.({
              el_abra_group: group.id,
              management: group.management,
              superintendency: group.superintendency,
              area: group.area,
            });
          }
        }
      },
    });
  };

  useEffect(() => {
    if (!showElAbraFields) {
      setElAbraGroups([]);
      setManagements([]);
      setSuperintendencies([]);
      setAreas([]);
      return;
    }

    reloadElAbraGroups();
    listManagements(1, {
      onSuccess: (resp: ServiceResponse) => {
        const list = resp?.data?.managements ?? resp?.data;
        if (Array.isArray(list)) setManagements(list);
      },
    } as AdicionalEvents);
    listSuperintendencies(1, {
      onSuccess: (resp: ServiceResponse) => {
        const list = resp?.data?.superintendencies ?? resp?.data;
        if (Array.isArray(list)) setSuperintendencies(list);
      },
    } as AdicionalEvents);
    listAreas(1, {
      onSuccess: (resp: ServiceResponse) => {
        const list = resp?.data?.areas ?? resp?.data;
        if (Array.isArray(list)) setAreas(list);
      },
    } as AdicionalEvents);
  }, [showElAbraFields, listElAbraGroups, listManagements, listSuperintendencies, listAreas]);

  const groupOptions = useMemo(
    () =>
      [...elAbraGroups]
        .sort((a, b) => compareEs(a.display_name, b.display_name))
        .map((g) => ({
          value: String(g.id),
          label: g.area_responsible?.trim()
            ? `${g.display_name} — ${g.area_responsible.trim()}`
            : g.display_name,
        })),
    [elAbraGroups]
  );

  const catalogsLoading =
    fetching.elAbraGroups ||
    fetching.managements ||
    fetching.superintendencies ||
    fetching.areas;

  const handleGroupModalSubmit = (payload: {
    management: number;
    superintendency: number;
    area: number;
  }) => {
    storeElAbraGroup(payload, {
      onSuccess: (resp: ServiceResponse) => {
        const newId = typeof resp?.data?.id === "number" ? resp.data.id : undefined;
        reloadElAbraGroups(newId);
        setGroupModalOpen(false);
        showToast("Grupo El Abra creado correctamente", "success");
      },
      onError: (resp: ServiceResponse) => {
        showToast(resp?.message ?? "No se pudo crear el grupo El Abra", "error");
      },
    });
  };

  const handleCatalogModalSubmit = (payload: {
    name?: string;
    area_responsible?: string;
  }) => {
    if (!catalogModalKind || !payload.name) return;

    const onSuccess = (resp: ServiceResponse) => {
      const id = Number(resp?.data?.id);
      if (!id) {
        showToast("El catálogo fue creado, pero no se pudo seleccionarlo automáticamente.", "info");
        setCatalogModalKind(null);
        return;
      }

      if (catalogModalKind === "management") {
        setManagements((current) => [...current, { id, name: payload.name! }]);
      } else if (catalogModalKind === "superintendency") {
        setSuperintendencies((current) => [...current, { id, name: payload.name! }]);
      } else {
        setAreas((current) => [
          ...current,
          { id, name: payload.name!, area_responsible: payload.area_responsible ?? "" },
        ]);
      }

      setSelectedCatalogItem({ kind: catalogModalKind, id });
      setCatalogModalKind(null);
      showToast(
        `${catalogModalKind === "management"
          ? "Gerencia"
          : catalogModalKind === "superintendency"
            ? "Superintendencia"
            : "Área y responsable"} creados correctamente`,
        "success"
      );
    };

    const onError = (resp: ServiceResponse) => {
      showToast(resp?.message ?? "No se pudo crear el registro del catálogo", "error");
    };

    if (catalogModalKind === "management") {
      storeManagement({ name: payload.name }, { onSuccess, onError });
    } else if (catalogModalKind === "superintendency") {
      storeSuperintendency({ name: payload.name }, { onSuccess, onError });
    } else {
      storeArea(
        { name: payload.name, area_responsible: payload.area_responsible },
        { onSuccess, onError }
      );
    }
  };

  const handleChange = (
    eOrName:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | string,
    val?: any
  ) => {
    if (typeof eOrName === "string") {
      onChange(eOrName as keyof Service, val);
    } else {
      const { name, value } = eOrName.target;
      onChange(name as keyof Service, value);
    }
  };

  const handleChangeCompany = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const companyId = Number(e.target.value);
    const nextCompanyName =
      companies.find((c) => String(c.value) === e.target.value)?.label ?? null;

    onChange("company", companyId);

    if (!isAdicionalEnabledForCompany(nextCompanyName)) {
      onPatch?.({
        el_abra_group: null,
        management: null,
        superintendency: null,
        area: null,
        service_reason_text: "",
      });
    }
  };

  const sortedCompanies = useMemo(() => {
    const sorted = [...companies].sort((a, b) => {
      const aLabel = String(a.label ?? "").toLowerCase();
      const bLabel = String(b.label ?? "").toLowerCase();
      const aIsEnaex = aLabel.includes("enaex");
      const bIsEnaex = bLabel.includes("enaex");
      const aIsAbra = aLabel.includes("abra");
      const bIsAbra = bLabel.includes("abra");

      if (aIsEnaex && !bIsEnaex) return -1;
      if (!aIsEnaex && bIsEnaex) return 1;
      if (aIsAbra && !bIsAbra) return -1;
      if (!aIsAbra && bIsAbra) return 1;
      return compareEs(a.label, b.label);
    });
    return sorted;
  }, [companies]);

  const handleGroupChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const raw = e.target.value;
    if (!raw) {
      onPatch?.({
        el_abra_group: null,
        management: null,
        superintendency: null,
        area: null,
      });
      return;
    }

    const groupId = Number(raw);
    const group = elAbraGroups.find((g) => g.id === groupId);
    if (!group) return;

    onPatch?.({
      el_abra_group: groupId,
      management: group.management,
      superintendency: group.superintendency,
      area: group.area,
    });
  };

  return (
    <div className="service-form-step1">
      <h3 className="form-title">Información Básica del Servicio</h3>

      <div className="form-grid service-form-step1__grid">
        <div className="form-field service-form-step1__field service-form-step1__field--company">
          <BaseSelect
            label="Empresa"
            name="company"
            value={String(value.company ?? "")}
            onChange={handleChangeCompany}
            placeholder="Seleccionar empresa"
            preserveCase
            searchable
            options={sortedCompanies.map((c) => ({
              value: String(c.value),
              label: c.label,
            }))}
          />
        </div>

        <div className="form-field service-form-step1__field service-form-step1__field--date">
          <AccessibleDatePicker
            id="service_date"
            name="service_date"
            label="Fecha del Servicio"
            value={value.service_date ?? ""}
            onChange={handleChange}
            minDateYmd={minDateYmd}
          />
          {isPastDate && (
            <p className="service-form-step1__date-error" role="alert">
              La fecha no es válida. Solo puedes crear servicios desde el {minDateYmd} en adelante.
            </p>
          )}
        </div>

        <div className="form-field service-form-step1__field service-form-step1__field--time">
          <AccessibleTimePicker
            id="service_time"
            name="service_time"
            label="Hora del Servicio"
            value={value.service_time ?? ""}
            onChange={handleChange}
          />
          {dateTimeInPast && (
            <p className="service-form-step1__date-error" role="alert">
              La fecha y hora del servicio no puede ser anterior a la hora actual. Ajusta la
              fecha u hora para continuar.
            </p>
          )}
        </div>

        {showElAbraFields && (
          <>
            <div className="service-form-step1__el-abra-divider" aria-hidden />

            <div className="form-field service-form-step1__field service-form-step1__field--el-abra service-form-step1__field--full service-form-step1__field--with-add">
              <div className="service-form-step1__field-head">
                <span className="service-form-step1__field-label">Grupo El Abra</span>
                <button
                  type="button"
                  className="service-form-step1__add-btn"
                  onClick={() => setGroupModalOpen(true)}
                  disabled={catalogsLoading || fetching.storeElAbraGroup}
                  title="Crear un grupo El Abra nuevo en el catálogo"
                >
                  <UserPlus size={16} strokeWidth={2.25} aria-hidden />
                  Nuevo
                </button>
              </div>
              <BaseSelect
                label=""
                name="el_abra_group"
                value={value.el_abra_group ? String(value.el_abra_group) : ""}
                onChange={handleGroupChange}
                placeholder={
                  catalogsLoading
                    ? "Cargando grupos…"
                    : groupOptions.length === 0
                      ? "Sin grupos — usa Nuevo"
                      : "Seleccionar grupo"
                }
                preserveCase
                searchable
                disabled={catalogsLoading}
                options={groupOptions}
              />
            </div>

            <div className="form-field service-form-step1__field service-form-step1__field--el-abra service-form-step1__field--full">
              <label className="service-form-step1__textarea-label" htmlFor="service_reason_text">
                Motivo del servicio
              </label>
              <textarea
                id="service_reason_text"
                name="service_reason_text"
                className="service-form-step1__textarea"
                value={value.service_reason_text ?? ""}
                onChange={handleChange}
                placeholder="Describa el motivo del servicio…"
                rows={4}
                maxLength={8000}
              />
            </div>

            {catalogsLoading && (
              <p className="service-form-step1__el-abra-loading" role="status">
                Cargando grupos El Abra…
              </p>
            )}
          </>
        )}
      </div>

      {showElAbraFields && (
        <ElAbraGroupModal
          isOpen={groupModalOpen}
          onClose={() => setGroupModalOpen(false)}
          onSubmit={handleGroupModalSubmit}
          loading={fetching.storeElAbraGroup}
          managements={managements}
          superintendencies={superintendencies}
          areas={areas}
          onAddCatalog={setCatalogModalKind}
          selectedCatalogItem={selectedCatalogItem}
        />
      )}

      {showElAbraFields && catalogModalKind && (
        <AdicionalCatalogModal
          isOpen
          onClose={() => setCatalogModalKind(null)}
          kind={catalogModalKind}
          onSubmit={handleCatalogModalSubmit}
          loading={
            fetching.storeManagement ||
            fetching.storeSuperintendency ||
            fetching.storeArea
          }
        />
      )}
    </div>
  );
};

export default ServiceFormStep1;
